/**
 * 队伍接口。
 * 契约：
 *   GET  /me/teams                         → {teams: TeamDto[]}（我拥有或已加入）
 *   POST /teams {name,eventCode?}          → {team}
 *   GET  /teams/:id                        → {team}
 *   POST /teams/:id/members {userId,role}  → {team}
 *   POST /teams/:id/members/:userId/accept → {team}
 *   PATCH /teams/:id/members/:userId/role  → {team}（T71：仅队长，成队后 409 冻结）
 *   PUT  /teams/:id/recruit {skills,note?} → {team}（T72：仅队长，成队后 409；skills 空数组 = 关闭）
 *   GET  /teams/recruiting?eventCode=      → {cards}（T72：正在招募的 forming 队伍，纯展示）
 *   POST /teams/:id/confirm                → {team, project, tasks}（成队即生成 4 条阶段待办）
 */
import type { TeamRole } from '@crossmatch/shared';
import { api } from './client.js';
import type { ConfirmTeamDto, HallListDto, RecruitingDto, TeamDto, TeamsDto } from './types.js';

export interface CreateTeamInput {
  name: string;
  eventCode?: string;
}

export interface InviteMemberInput {
  /** 对方的邀请 ID（CM-XXXXXX）——推荐，可口头传播 */
  code?: string;
  /** 对方的 userId（UUID）——兼容旧用法（如从候选人池复制） */
  userId?: string;
  role: TeamRole;
}

export const teamsApi = {
  listMine: (): Promise<TeamsDto> => api.get<TeamsDto>('/me/teams'),

  create: (input: CreateTeamInput): Promise<{ team: TeamDto }> => api.post<{ team: TeamDto }>('/teams', input),

  get: (teamId: string): Promise<{ team: TeamDto }> => api.get<{ team: TeamDto }>(`/teams/${encodeURIComponent(teamId)}`),

  invite: (teamId: string, input: InviteMemberInput): Promise<{ team: TeamDto }> =>
    api.post<{ team: TeamDto }>(`/teams/${encodeURIComponent(teamId)}/members`, input),

  accept: (teamId: string, userId: string): Promise<{ team: TeamDto }> =>
    api.post<{ team: TeamDto }>(
      `/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}/accept`,
    ),

  /** 改成员角色（T71：仅队长；确认成队后后端 409 冻结） */
  setRole: (teamId: string, userId: string, role: TeamRole): Promise<{ team: TeamDto }> =>
    api.patch<{ team: TeamDto }>(
      `/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}/role`,
      { role },
    ),

  /** 设置/清除招募需求（T72：仅队长；skills 空数组 = 关闭招募） */
  setRecruit: (teamId: string, input: { skills: string[]; note?: string }): Promise<{ team: TeamDto }> =>
    api.put<{ team: TeamDto }>(`/teams/${encodeURIComponent(teamId)}/recruit`, input),

  /** 正在招募的队伍卡片（T72：纯展示区块） */
  listRecruiting: (eventCode?: string): Promise<RecruitingDto> =>
    api.get<RecruitingDto>(`/teams/recruiting${eventCode ? `?eventCode=${encodeURIComponent(eventCode)}` : ''}`),

  /** 组队大厅卡片（扩展②：双向选择） */
  listHall: (eventCode?: string): Promise<HallListDto> =>
    api.get<HallListDto>(`/teams/hall${eventCode ? `?eventCode=${encodeURIComponent(eventCode)}` : ''}`),

  /** 申请加入（扩展②：open=直接入队；approval=待审批） */
  join: (teamId: string, message?: string): Promise<{ team: TeamDto }> =>
    api.post<{ team: TeamDto }>(`/teams/${encodeURIComponent(teamId)}/join`, { message }),

  /** 审批加入申请（扩展②：仅队长） */
  approve: (teamId: string, userId: string): Promise<{ team: TeamDto }> =>
    api.post<{ team: TeamDto }>(`/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}/approve`),

  /** 拒绝加入申请（扩展②：仅队长） */
  decline: (teamId: string, userId: string): Promise<{ team: TeamDto }> =>
    api.post<{ team: TeamDto }>(`/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}/decline`),

  /** 修改准入档（扩展②：仅队长，forming 阶段） */
  setJoinPolicy: (teamId: string, policy: 'open' | 'approval' | 'invite_only'): Promise<{ team: TeamDto }> =>
    api.put<{ team: TeamDto }>(`/teams/${encodeURIComponent(teamId)}/join-policy`, { policy }),

  confirm: (teamId: string): Promise<ConfirmTeamDto> =>
    api.post<ConfirmTeamDto>(`/teams/${encodeURIComponent(teamId)}/confirm`),
};